import { Link, useRouter } from 'expo-router';
import Animated, { FadeIn, FadeInDown } from 'react-native-reanimated';
import { ScrollView, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { PrimaryButton } from '@/components/buttons';
import { RosterStrip } from '@/components/roster-strip';
import { APP_VERSION } from '@/constants/version';
import { COHORTS } from '@/game/cohorts';
import { useGame } from '@/game/game-context';
import { STARTING_REROLLS, TOTAL_ROUNDS } from '@/game/session';
import { MAX_WIDTH, colors, radius, type } from '@/theme';

/** Centered caps label with a short gold underline — the section divider. */
function SectionRule({ label }: { label: string }) {
  return (
    <View style={styles.rule}>
      <Text style={styles.ruleLabel}>{label}</Text>
      <View style={styles.ruleLine} />
    </View>
  );
}

function StatPill({ label }: { label: string }) {
  return (
    <View style={styles.pill}>
      <Text style={styles.pillLabel}>{label}</Text>
    </View>
  );
}

/**
 * Diameters for the fake radial glow, largest first, all centred on
 * GLOW_CENTER_Y. Many small steps — few large ones band visibly on a wide
 * canvas.
 */
const GLOW_RINGS = Array.from({ length: 16 }, (_, i) => 880 - i * 52);
const GLOW_CENTER_Y = 150;

/**
 * No gradient library ships with the app, so the glow is concentric gold discs
 * stacked largest-first. Each ring is faint enough that its edge is invisible;
 * the accumulated alpha toward the centre is what reads as a glow.
 */
function Glow() {
  return (
    <Animated.View
      entering={FadeIn.duration(900)}
      pointerEvents="none"
      style={[StyleSheet.absoluteFill, styles.glow]}>
      {GLOW_RINGS.map((size) => (
        <View
          key={size}
          style={[
            styles.glowRing,
            {
              width: size,
              height: size,
              borderRadius: size / 2,
              top: GLOW_CENTER_Y - size / 2,
            },
          ]}
        />
      ))}
    </Animated.View>
  );
}

/**
 * The pages the app stores link to. Rendered as `<Link>` so the web build emits
 * real anchors — crawlers and store reviewers both follow them from the landing
 * page rather than needing the URLs handed to them.
 */
const LEGAL_LINKS = [
  { href: '/privacy', label: 'Privacy' },
  { href: '/terms', label: 'Terms' },
  { href: '/support', label: 'Support' },
] as const;

/** Below this height the hero shrinks so the CTA still lands above the fold. */
const COMPACT_HEIGHT = 740;

export default function TitleScreen() {
  const router = useRouter();
  const { start } = useGame();
  const compact = useWindowDimensions().height < COMPACT_HEIGHT;

  const onStart = () => {
    start();
    router.push('/draft');
  };

  return (
    <SafeAreaView style={styles.safe}>
      <Glow />
      <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
        <View style={[styles.container, compact && styles.containerCompact]}>
          <Animated.View entering={FadeIn.duration(400)} style={styles.hero}>
            <Text style={styles.kicker}>A TECH DRAFTING GAME</Text>
            <Text style={[styles.wordmark, compact && styles.wordmarkCompact]}>
              1<Text style={styles.wordmarkAccent}>-</Text>T
            </Text>
            <Text style={[styles.headline, compact && styles.headlineCompact]}>
              Can you build a <Text style={styles.headlineAccent}>$1T</Text> company?
            </Text>
            <Text style={[styles.tagline, compact && styles.taglineCompact]}>
              Draft six legends of tech. Put them in the right seats.
            </Text>
          </Animated.View>

          <Animated.View entering={FadeInDown.delay(220).duration(400)} style={styles.board}>
            <SectionRule label="YOUR STARTING SIX" />
            <RosterStrip roster={{}} />
            <View style={styles.pills}>
              <StatPill label={`${TOTAL_ROUNDS} ROUNDS`} />
              <StatPill label={`${STARTING_REROLLS} REROLLS`} />
              <StatPill label={`${COHORTS.length} COHORTS`} />
            </View>
          </Animated.View>

          <Animated.View entering={FadeInDown.delay(400).duration(400)} style={styles.cta}>
            <PrimaryButton label="START DRAFT" onPress={onStart} />
            <View style={styles.ctaMeta}>
              <Text style={styles.footnote}>Almost nobody reaches $1T.</Text>
              <View style={styles.legalNav}>
                {LEGAL_LINKS.map(({ href, label }) => (
                  <Link key={href} href={href} style={styles.legalLink}>
                    {label}
                  </Link>
                ))}
              </View>
              <Text style={styles.version}>{APP_VERSION}</Text>
            </View>
          </Animated.View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  scroll: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
    width: '100%',
    maxWidth: MAX_WIDTH,
    alignSelf: 'center',
    paddingHorizontal: 20,
    paddingVertical: 24,
    gap: 34,
  },
  containerCompact: {
    paddingVertical: 14,
    gap: 18,
  },
  glow: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'hidden',
  },
  glowRing: {
    position: 'absolute',
    backgroundColor: 'rgba(242, 203, 78, 0.009)',
  },
  hero: {
    gap: 6,
  },
  kicker: {
    ...type.label,
    color: colors.faint,
  },
  wordmark: {
    ...type.display,
    fontSize: 84,
    lineHeight: 88,
    letterSpacing: -5,
    color: colors.text,
  },
  wordmarkCompact: {
    fontSize: 58,
    lineHeight: 62,
    letterSpacing: -3.4,
  },
  wordmarkAccent: {
    color: colors.gold,
  },
  headline: {
    ...type.display,
    fontSize: 38,
    lineHeight: 42,
    color: colors.text,
    marginTop: 4,
    maxWidth: 340,
  },
  headlineCompact: {
    fontSize: 28,
    lineHeight: 32,
    marginTop: 0,
  },
  headlineAccent: {
    color: colors.gold,
  },
  tagline: {
    ...type.body,
    fontSize: 17,
    lineHeight: 25,
    color: colors.dim,
    maxWidth: 340,
    marginTop: 10,
  },
  taglineCompact: {
    fontSize: 15,
    lineHeight: 21,
    marginTop: 6,
  },
  /** Auto margins here and on `cta` split the tall-screen slack evenly. */
  board: {
    marginTop: 'auto',
    gap: 14,
  },
  rule: {
    alignItems: 'center',
    gap: 8,
  },
  ruleLabel: {
    ...type.label,
    fontSize: 10,
    color: colors.gold,
  },
  ruleLine: {
    width: 64,
    height: 2,
    borderRadius: radius.pill,
    backgroundColor: colors.gold,
  },
  pills: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 8,
    marginTop: 4,
  },
  pill: {
    borderRadius: radius.pill,
    borderWidth: 1,
    borderColor: colors.borderSoft,
    backgroundColor: colors.card,
    paddingHorizontal: 12,
    paddingVertical: 7,
  },
  pillLabel: {
    ...type.label,
    fontSize: 10,
    color: colors.dim,
  },
  cta: {
    marginTop: 'auto',
    backgroundColor: colors.card,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: colors.border,
    padding: 16,
    gap: 12,
  },
  ctaMeta: {
    alignItems: 'center',
    gap: 4,
  },
  footnote: {
    ...type.body,
    fontSize: 13,
    color: colors.faint,
    textAlign: 'center',
  },
  legalNav: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 16,
    marginTop: 2,
  },
  legalLink: {
    ...type.label,
    fontSize: 9,
    color: colors.faint,
  },
  /** Build + OTA stamp, so a screenshot is enough to identify the bundle. */
  version: {
    ...type.label,
    fontSize: 9,
    color: colors.faint,
  },
});
